import { useCallback, useState } from 'react'

import i18n from 'modules/core/lib/i18n'
import appStore from 'modules/core/store/models/app'

import { useDeleteWorkAreaMapSpaceLocation } from 'modules/space_control/hooks/useDeleteWorkAreaMapSpaceLocation'
import { useUpsertWorkAreaMapSpaceLocation } from 'modules/space_control/hooks/useUpsertWorkAreaMapSpaceLocation'
import { WorkSpace } from 'modules/space_control/hooks/useWorkSpace'
import { WorkSpaceWithBookingNode } from 'modules/space_control/hooks/useWorkSpacesWithBookings'

export const SETTINGS_EVENT = {
  PIN_PLACED: 'settings:pinPlaced',
  PIN_DELETED: 'settings:pinDeleted',
  PIN_HOVERED: 'settings:pinHovered',
} as const

export type SettingsEventType = (typeof SETTINGS_EVENT)[keyof typeof SETTINGS_EVENT]

interface UseSettingsMapEventsProps {
  selectedWorkSpace?: WorkSpace | null
  mapId?: number
  workSpaces: WorkSpaceWithBookingNode[]
  onEvent?: (event: SettingsEventType, payload?: Record<string, unknown>) => void
}

export const useSettingsMapEvents = ({
  selectedWorkSpace,
  mapId,
  workSpaces,
  onEvent,
}: UseSettingsMapEventsProps) => {
  const { upsertWorkAreaMapSpaceLocation, loading: isPlacingPin } =
    useUpsertWorkAreaMapSpaceLocation()
  const { deleteWorkAreaMapSpaceLocation } = useDeleteWorkAreaMapSpaceLocation()
  const [alertWorkSpace, setAlertWorkSpace] = useState<WorkSpaceWithBookingNode | null>(null)
  const [isDeletingPin, setIsDeletingPin] = useState(false)

  const handleMapClick = useCallback(
    async (coordinates: { x: number; y: number }) => {
      setAlertWorkSpace(null)
      if (!selectedWorkSpace?.id || !mapId) return

      try {
        await upsertWorkAreaMapSpaceLocation({
          workAreaMapId: mapId,
          workSpaceId: selectedWorkSpace.id,
          x: coordinates.x,
          y: coordinates.y,
        })

        appStore.setFlash({
          type: 'correct',
          message: i18n.t('space_control.map.pin_placed_successfully', {
            workspaceName: selectedWorkSpace.name,
          }),
        })

        onEvent?.(SETTINGS_EVENT.PIN_PLACED)
      } catch (error) {
        appStore.setFlash({
          type: 'wrong',
          message: i18n.t('common.error'),
        })
      }
    },
    [selectedWorkSpace, mapId, upsertWorkAreaMapSpaceLocation, onEvent]
  )

  const handlePinClick = useCallback(
    ({ workSpaceId }: { workSpaceId: number }) => {
      const ws = workSpaces.find((w) => w.workSpaceId === workSpaceId)
      if (!ws?.mapSpaceLocation) return

      setAlertWorkSpace((prev) => {
        if (prev?.workSpaceId === workSpaceId) return null
        return ws
      })
    },
    [workSpaces]
  )

  const handleDelete = useCallback(async () => {
    if (!alertWorkSpace?.mapSpaceLocation?.id) return

    setIsDeletingPin(true)
    try {
      await deleteWorkAreaMapSpaceLocation({ id: alertWorkSpace.mapSpaceLocation.id })
      setAlertWorkSpace(null)
      onEvent?.(SETTINGS_EVENT.PIN_DELETED)
    } finally {
      setIsDeletingPin(false)
    }
  }, [alertWorkSpace, deleteWorkAreaMapSpaceLocation, onEvent])

  const dismissAlert = useCallback(() => {
    setAlertWorkSpace(null)
  }, [])

  const handleHover = useCallback(
    ({ workSpaceId }: { workSpaceId: number | null }) => {
      onEvent?.(SETTINGS_EVENT.PIN_HOVERED, { workSpaceId: workSpaceId || null })
    },
    [onEvent]
  )

  return {
    actions: {
      onPinClick: handlePinClick,
      onMapClick: handleMapClick,
      onPinHover: handleHover,
    },
    isPlacingPin,
    isDeletingPin,
    alertWorkSpace,
    handleDelete,
    dismissAlert,
  }
}
